import RenderIf from "common/components/RenderIf";
import ProfileImage from "common/components/ui/ProfileImage";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

interface AccountAvatarsTooltipProps {
  accounts: WorkspaceAccount[];
  totalCount: number;
}

export default function AccountAvatarsTooltip({
  accounts,
  totalCount,
}: AccountAvatarsTooltipProps) {
  const isDesktop = useTwBreakpoint("lg");

  const maxAccounts = totalCount === 11 ? 11 : 10;
  const pendingAccountsCount = totalCount - accounts.length;

  const combinedAccounts = [
    ...accounts,
    ...Array.from({ length: Math.max(0, pendingAccountsCount) }, (_, i) => ({
      type: "pending" as const,
      id: `pending-account-${i + 1}`,
    })),
  ];

  return (
    <Tooltip>
      <TooltipTrigger className="flex -space-x-2.5">
        {combinedAccounts
          .slice(0, maxAccounts)
          .map((item) =>
            item.type === "pending" ? (
              <span
                key={item.id}
                className="size-6 bg-black-50 rounded-full border-black-400 border border-dashed"
              />
            ) : (
              <ProfileImage
                key={item.id}
                src={item.picture_url}
                alt={item.full_name}
                size="xs"
                className="ring-1 ring-white"
              />
            ),
          )}

        <RenderIf condition={totalCount > maxAccounts}>
          <span className="inline-flex ring-1 ring-white bg-purple-100 size-6 rounded-full items-center justify-center text-purple-600 text-xxs-bold">
            +{totalCount - maxAccounts}
          </span>
        </RenderIf>
      </TooltipTrigger>

      <TooltipContent side={isDesktop ? "right" : "top"}>
        <div className="flex flex-col gap-y-1.5 max-h-60 overflow-y-auto scrollbar-thin -my-4 py-4">
          {accounts.map(({ id, picture_url, full_name }) => (
            <div key={id} className="flex items-center gap-x-1">
              <ProfileImage size="2xs" src={picture_url} />
              <span className="text-xs">{full_name}</span>
            </div>
          ))}

          <RenderIf condition={pendingAccountsCount > 0}>
            <span className="text-xs">
              {accounts.length > 0 && "+ "}
              {pendingAccountsCount} Account
              {pendingAccountsCount > 1 ? "s" : ""} pending
            </span>
          </RenderIf>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
